import { useMemo } from 'react';
import { useSupabaseAuth } from '@/hooks/auth';
import { useUserCourseAssignments } from './useUserCourseAssignments';
import { useUnifiedCourses } from './useUnifiedCourses';

/**
 * Hook that filters courses based on user assignments and role.
 * 
 * SECURITY: Two-tier course access control
 * 1. Visibility Filtering (this hook): Controls what courses appear in navigation/UI
 *    - Students: Only see courses they're assigned to
 *    - Admins/Staff: See all courses for management purposes
 * 
 * 2. Content Access Blocking (CourseAccessGuard): Prevents unauthorized access to course content
 *    - Even if a student somehow navigates to an unassigned course URL, they'll be blocked
 * 
 * @param category - Optional category filter
 * @returns Filtered courses based on user's role and assignments
 */
export const useAssignedCoursesOnly = (category?: string) => {
  const { hasAccess, userRole } = useSupabaseAuth();
  const { assignments, loading: assignmentsLoading } = useUserCourseAssignments();
  const { courses, loading: coursesLoading, error, source, refetch } = useUnifiedCourses(category);
  
  // DEBUG: Log hook execution at the top
  console.log('🔍 [useAssignedCoursesOnly] Hook called:', {
    category,
    userRole,
    isAdmin: hasAccess('admin'),
    isStaff: hasAccess('staff'),
    totalCourses: courses.length,
    assignmentsCount: assignments.length,
    assignmentsLoading,
    coursesLoading
  });
  
  // Filter courses based on user role and assignments
  const filteredCourses = useMemo(() => {
    console.log('🔍 [useAssignedCoursesOnly] useMemo executing:', {
      coursesLength: courses.length,
      assignmentsLength: assignments.length,
      assignmentsLoading,
      userRole
    });
    // Admins and staff can see all courses for management purposes
    if (hasAccess('admin') || hasAccess('staff')) {
      console.log('✅ [useAssignedCoursesOnly] Admin/Staff access - showing all courses:', courses.length);
      return courses;
    }
    
    // For regular users (students), only show assigned courses
    if (assignmentsLoading) {
      console.log('⏳ [useAssignedCoursesOnly] Assignments still loading - returning empty array');
      // While loading assignments, don't show any courses to avoid flashing all courses
      return [];
    }
    
    // Get list of assigned course IDs
    const assignedCourseIds = new Set(assignments.map(a => a.course_id));
    
    console.log('📋 [useAssignedCoursesOnly] Assigned course IDs:', Array.from(assignedCourseIds));
    
    // Filter courses to only include assigned ones
    const filteredList = courses.filter(course => assignedCourseIds.has(course.id));
    
    console.log('✅ [useAssignedCoursesOnly] Course filtering applied:', {
      userRole,
      totalCourses: courses.length,
      assignedCourses: assignedCourseIds.size,
      filteredCourses: filteredList.length,
      filteredCourseIds: filteredList.map(c => c.id),
      isAdmin: hasAccess('admin'),
      isStaff: hasAccess('staff')
    });
    
    // Log missing courses (assigned but not found in database)
    const missingCourses = Array.from(assignedCourseIds).filter(id => 
      !courses.some(course => course.id === id)
    );
    if (missingCourses.length > 0) {
      console.warn('⚠️ [useAssignedCoursesOnly] Assigned courses not found in database:', missingCourses);
    }
    
    return filteredList;
  }, [courses, assignments, assignmentsLoading, hasAccess, userRole, assignments.length]);
  
  // Combine loading states
  const loading = coursesLoading || assignmentsLoading;
  
  return {
    courses: filteredCourses,
    loading,
    assignmentsLoading, // Expose assignments loading state separately
    error,
    source,
    refetch,
    totalAvailableCourses: filteredCourses.length,
    isFiltered: !hasAccess('admin') && !hasAccess('staff'), // Indicates if filtering was applied
    assignmentCount: assignments.length
  };
};
